import React from 'react';
import SectionWrapper from '../UI/SectionWrapper';
import { Phone, Map, ChevronRight, Gem, Sparkles, Wind, Sun, Activity, Sprout } from 'lucide-react';

const WhatToExpect = () => {
  return (
    <SectionWrapper id="what-to-expect" className="py-24 bg-white relative overflow-hidden">
        {/* Decorative background blur */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-forest-green/20 to-transparent"></div>
        <div className="absolute -top-20 left-1/4 w-96 h-96 bg-emerald-50/50 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Unified Main Header */}
        <div className="text-center mb-16 relative z-10">
             <h2 className="text-5xl md:text-6xl font-serif text-forest-green mb-4">
                What to Expect
            </h2>
            <div className="w-24 h-1.5 bg-forest-green/10 mx-auto rounded-full"></div>
        </div>

        {/* 3-Column Layout: Process -> Philosophy -> Outcome */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            
            {/* Column 1: From an Earth Healing */}
            <div className="group bg-stone-50/50 hover:bg-stone-50 rounded-3xl p-8 border border-stone-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col">
                <div className="mb-6 flex items-center gap-3">
                     <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-serif font-bold">1</span>
                    <h3 className="text-2xl font-serif text-forest-green">
                        ...from an <span className="italic">Earth Healing</span>
                    </h3>
                </div>
                
                <div className="space-y-6 flex-grow">
                     <p className="text-stone-600 leading-relaxed font-sans">
                        We can connect on the phone before the Land Healing takes place to answer any questions and concerns you may have.
                    </p>
                    <div className="w-full h-px bg-stone-200/50"></div>
                    <p className="text-stone-600 leading-relaxed font-sans">
                        Within 14 days of payment a map is created locating ley lines, geographic stress that has been uncovered. You will receive a report which includes the map, a summary of the findings and the list of crystals and locations for their placement. 
                    </p>
                    <p className="text-stone-600 leading-relaxed font-sans">
                        The crystals will then be sent to you and we will have a follow-up phone consultation to discuss the instructions and also other recommendations for integration.
                    </p>
                </div>
            </div>

            {/* Column 2: Why Crystals? */}
             <div className="group bg-gradient-to-br from-indigo-50/50 to-purple-50/50 hover:from-indigo-50 hover:to-purple-50 rounded-3xl p-8 border border-indigo-100/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col relative overflow-hidden">
                {/* Decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100 rounded-full blur-2xl -mr-16 -mt-16 opacity-50"></div>

                <div className="mb-6 flex items-center gap-3 relative z-10">
                     <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-serif font-bold">2</span>
                     <h3 className="text-2xl font-serif text-forest-green">
                        Why <span className="italic">Crystals?</span>
                    </h3>
                </div>

                <div className="space-y-6 flex-grow relative z-10">
                    <div className="flex justify-center py-4">
                        <Gem size={48} strokeWidth={1} className="text-indigo-400 drop-shadow-md" />
                    </div>
                     <p className="text-stone-600 leading-relaxed font-sans">
                        The goal of land healing is to restore harmony by balancing energy distortions. This is achieved by placing small crystals in carefully considered locations, combined with clear intention to benefit the land and people to their highest good.
                    </p>
                    <p className="text-stone-600 leading-relaxed font-sans">
                        Crystals, long valued in both technology and tradition, have the unique ability to amplify and transform frequency due to their atomic structure—a simple yet profound science.
                    </p>
                </div>
            </div>

            {/* Column 3: After the Healing */}
             <div className="group bg-amber-50/30 hover:bg-amber-50/50 rounded-3xl p-8 border border-amber-100/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col">
                <div className="mb-6 flex items-center gap-3">
                     <span className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-serif font-bold">3</span>
                    <h3 className="text-2xl font-serif text-forest-green">
                        ...after the <span className="italic">Healing?</span>
                    </h3>
                </div>

                <div className="space-y-4 flex-grow">
                     
                     <div className="flex gap-3 items-start">
                        <div className="bg-amber-100/50 p-1.5 rounded-lg text-amber-600 mt-0.5 shrink-0"><Sparkles size={16} /></div>
                        <p className="text-stone-600 text-sm leading-relaxed">Some report instantaneous flow of improvements, often before the crystals even go in the ground.</p>
                     </div>

                     <div className="flex gap-3 items-start">
                        <div className="bg-amber-100/50 p-1.5 rounded-lg text-amber-600 mt-0.5 shrink-0"><Sun size={16} /></div>
                        <p className="text-stone-600 text-sm leading-relaxed">Sudden remedy of a situation</p>
                     </div>

                     <div className="flex gap-3 items-start">
                        <div className="bg-amber-100/50 p-1.5 rounded-lg text-amber-600 mt-0.5 shrink-0"><Wind size={16} /></div>
                         <p className="text-stone-600 text-sm leading-relaxed">Gradual improvement and sense of ease and enjoyment from the environment</p>
                     </div>

                     <div className="flex gap-3 items-start">
                        <div className="bg-amber-100/50 p-1.5 rounded-lg text-amber-600 mt-0.5 shrink-0"><Activity size={16} /></div>
                        <p className="text-stone-600 text-sm leading-relaxed">People can report a mild cold or flu symptoms, or simply feeling ‘off’. This is normal part of the body recalibrating and is a natural part of the process.</p>
                     </div>

                     <div className="flex gap-3 items-start">
                        <div className="bg-amber-100/50 p-1.5 rounded-lg text-amber-600 mt-0.5 shrink-0"><Sprout size={16} /></div>
                        <p className="text-stone-600 text-sm leading-relaxed">This process also extends out and benefits all surrounding properties, neighbourhoods, the earth and collective consciousness.</p>
                     </div>

                </div>
            </div>

        </div>

      </div>
    </SectionWrapper>
  );
};

export default WhatToExpect;
